// askSmart-ui/src/pages/TOSP/components/LeasedSpaceCard.tsx
import React from "react";
import { formatCurrency, formatSqFt } from "../format";
import {
  LeasedSpaceDraft,
  OccupancyDraft,
  RentResetDraft,
  draftEscalation,
  emptyRentReset,
} from "../occupancyDraft";
import { useTospData } from "../TospDataContext";
import {
  bmvaRate,
  rentPsfCurrent,
  rentPsfStepUp,
  rentPsfToday,
  RENT_STEP_UP_MONTHS,
  Unit,
} from "../types";

export interface UnitOption {
  unit: Unit;
  /** "[Building]-[Suite]". */
  label: string;
  /**
   * The tenant currently holding the unit. Still selectable — choosing it
   * starts a takeover rather than a clash.
   */
  occupiedBy?: string;
  /** Held by another space on this same tenant — nothing to take over. */
  disabled?: boolean;
}


interface LeasedSpaceCardProps {
  /** The lease this space belongs to — its dates and rates govern here. */
  occupancy: OccupancyDraft;
  draft: LeasedSpaceDraft;
  /** 1-based, for the card title before the space is named. */
  position: number;
  expanded: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<LeasedSpaceDraft>) => void;
  onRemove: () => void;
  unitOptions: UnitOption[];
  unitById: (unitId: string) => Unit | undefined;
  /** Validation messages for this space, keyed by field. */
  errors: Record<string, string>;
  readOnly?: boolean;
}

// One leased space, edited in place inside its lease.
//
// This is the rent record: everything whose answer differs from one area of a
// lease to another. The dates, the term, the clauses and the two percentage
// rent rates belong to the agreement and live on the card above.
export default function LeasedSpaceCard({
  occupancy,
  draft,
  position,
  expanded,
  onToggle,
  onChange,
  onRemove,
  unitOptions,
  unitById,
  errors,
  readOnly = false,
}: LeasedSpaceCardProps) {
  const { configuration } = useTospData();

  const set = (key: keyof LeasedSpaceDraft, value: string) =>
    onChange({ [key]: value } as Partial<LeasedSpaceDraft>);

  const setReset = (id: string, patch: Partial<RentResetDraft>) =>
    onChange({
      rentResets: draft.rentResets.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    });

  // Space Type and Occupancy SqFt are the unit's, shown here for context and
  // never copied onto the lease.
  const unit = draft.unitId ? unitById(draft.unitId) : undefined;
  const unitLabel = unitOptions.find((o) => o.unit.id === draft.unitId)?.label;
  const hasErrors = Object.keys(errors).length > 0;
  const title = draft.name.trim() || unitLabel || `Leased Space ${position}`;

  // Recomputed on every keystroke: the rates track this space's commencement
  // rate and increase, against the lease's own commencement date.
  const escalation = draftEscalation(occupancy, draft);

  // BMVA charges the same royalty to everybody, so the rate is read from
  // Configuration; the cap is this space's own.
  const bmvaCapUsed = draft.bmvaCap.trim() !== "";
  const royalty = configuration.bmvaRoyaltyPercentage;

  // The three calculated rates sit a column right of where they'd naturally
  // fall, leaving the first column for a renewal's rate. That column is only
  // worth reserving once there's a renewal to put in it.
  const psfColumn = (column: number) =>
    `tosp-col-${draft.rentResets.length > 0 ? column + 1 : column}`;

  const fieldId = (key: string) => `tosp-spc-${draft.id}-${key}`;

  const numberField = (
    key: keyof LeasedSpaceDraft,
    label: string,
    unitAffix: "$" | "%" | null = null,
    highlight = false,
  ) => (
    <div className="tosp-field">
      <label className="tosp-label" htmlFor={fieldId(key)}>
        {label}
      </label>
      <div className="tosp-affix-wrap">
        {unitAffix === "$" && <span className="tosp-affix tosp-affix-prefix">$</span>}
        <input
          id={fieldId(key)}
          className={`manage-input${
            unitAffix === "$"
              ? " tosp-has-prefix"
              : unitAffix === "%"
                ? " tosp-has-suffix"
                : ""
          }${highlight ? " tosp-input-live" : ""}`}
          autoComplete="off"
          inputMode="decimal"
          disabled={readOnly}
          value={draft[key] as string}
          onChange={(e) => set(key, e.target.value)}
        />
        {unitAffix === "%" && <span className="tosp-affix tosp-affix-suffix">%</span>}
      </div>
      {errors[key] && <p className="tosp-field-error">{errors[key]}</p>}
    </div>
  );

  const calcMoneyField = (
    label: string,
    value: number | null,
    hint: string,
    className = "",
    highlight = false,
  ) => (
    <div className={`tosp-field${className ? ` ${className}` : ""}`}>
      <span className="tosp-label">{label}</span>
      <div
        className={`tosp-calc-value${highlight ? " tosp-calc-value-live" : ""}`}
        title={hint}
      >
        {value === null ? "—" : formatCurrency(value)}
      </div>
    </div>
  );

  // A renewal's half of the pair, dropped into a free slot on an existing row
  // rather than a row of its own. Renders nothing until the first "+".
  const renewalSlot = (
    columnClass: string,
    label: string,
    renderEntry: (reset: RentResetDraft, index: number) => React.ReactNode,
  ) =>
    draft.rentResets.length === 0 ? null : (
      <div className={`tosp-field ${columnClass}`}>
        <span className="tosp-label tosp-renewal-label">{label}</span>
        <div className="tosp-renewal-stack">{draft.rentResets.map(renderEntry)}</div>
      </div>
    );

  return (
    <div
      className={`tosp-accordion tosp-space-card${
        hasErrors ? " tosp-accordion-error" : ""
      }`}
    >
      <div className="tosp-accordion-header">
        <button
          type="button"
          className="tosp-accordion-toggle"
          onClick={onToggle}
          aria-expanded={expanded}
          title={expanded ? "Collapse" : "Expand"}
        >
          <span className="tosp-accordion-caret">{expanded ? "▾" : "▸"}</span>
          <span className="tosp-accordion-title">{title}</span>
          {/* Whether the space is billed at all is the one thing worth seeing
              without opening it. */}
          <span
            className={`tosp-space-badge${
              draft.unitId ? "" : " tosp-space-badge-unassigned"
            }`}
          >
            {draft.unitId ? unitLabel : "Not assigned"}
          </span>
        </button>
        {!readOnly && (
          <button
            type="button"
            className="tosp-btn-secondary tosp-btn-danger"
            onClick={onRemove}
            title="Remove this leased space"
          >
            Remove
          </button>
        )}
      </div>

      {expanded && (
        <div className="tosp-accordion-body">
          <div className="tosp-form-row-4">
            <div className="tosp-field">
              <label className="tosp-label" htmlFor={fieldId("name")}>
                Area Name
              </label>
              <input
                id={fieldId("name")}
                className="manage-input"
                autoComplete="off"
                placeholder="e.g. Ground Floor"
                disabled={readOnly}
                value={draft.name}
                onChange={(e) => set("name", e.target.value)}
              />
              {errors.name && <p className="tosp-field-error">{errors.name}</p>}
            </div>

            {/* Optional on purpose. A lease may grant an area that generates
                neither rent nor taxes — a terrace, usually — and that is a
                recorded fact about the agreement, not an unfinished record. */}
            <div className="tosp-field">
              <label className="tosp-label" htmlFor={fieldId("unitId")}>
                Unit Name
              </label>
              <select
                id={fieldId("unitId")}
                className="manage-input"
                disabled={readOnly}
                value={draft.unitId}
                onChange={(e) => set("unitId", e.target.value)}
              >
                <option value="">— Not assigned —</option>
                {unitOptions.map(({ unit: option, label, occupiedBy, disabled }) => (
                  <option key={option.id} value={option.id} disabled={disabled}>
                    {label}
                    {occupiedBy ? ` — occupied by ${occupiedBy}` : ""}
                  </option>
                ))}
              </select>
              {errors.unitId && <p className="tosp-field-error">{errors.unitId}</p>}
            </div>

            <div className="tosp-field">
              <span className="tosp-label">Space Type</span>
              <div className="tosp-calc-value" title="Set on the unit">
                {unit?.spaceType || "—"}
              </div>
            </div>

            <div className="tosp-field">
              <span className="tosp-label">Occupancy SqFt</span>
              <div
                className="tosp-calc-value tosp-calc-value-live"
                title="Set on the unit — what rent and fees are charged on"
              >
                {unit ? formatSqFt(unit.occupancySqFt) : "—"}
              </div>
            </div>
          </div>

          {/* Whether this space escalates at all, and by how much, on one
              line. Off is a real answer — a storage locker at a flat rate
              never steps up — and the three boxes are hidden rather than left
              blank, so the card can't be read as an unfinished record. */}
          <div className="tosp-form-row-4">
            <div className="tosp-field">
              <span className="tosp-label">Annual Increase</span>
              <label className="switch tosp-inline-switch">
                <input
                  type="checkbox"
                  checked={draft.annualIncreaseEnabled}
                  disabled={readOnly}
                  onChange={(e) => onChange({ annualIncreaseEnabled: e.target.checked })}
                />
                <span className="slider" />
              </label>
            </div>

            {draft.annualIncreaseEnabled && (
              <>
                {numberField("minimumIncrease", "Minimum", "%", true)}
                {numberField("maximumIncrease", "Maximum", "%")}
                <div className="tosp-field">
                  <label className="tosp-label" htmlFor={fieldId("rentStepUpMonth")}>
                    Rent Step Up Month
                  </label>
                  <select
                    id={fieldId("rentStepUpMonth")}
                    className="manage-input"
                    disabled={readOnly}
                    value={draft.rentStepUpMonth}
                    onChange={(e) => set("rentStepUpMonth", e.target.value)}
                  >
                    <option value="">— Select a month —</option>
                    {RENT_STEP_UP_MONTHS.map((month) => (
                      <option key={month} value={month}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>

          <div className="tosp-form-row-4">
            {/* "+" adds a renewal: a fresh rent commencement that supersedes
                this one from its own date onward. */}
            <div className="tosp-field">
              <label className="tosp-label" htmlFor={fieldId("rentCommenceDate")}>
                Rent Commence Date
                {!readOnly && (
                  <button
                    type="button"
                    className="tosp-inline-add"
                    title="Add a rent renewal"
                    onClick={() =>
                      onChange({ rentResets: [...draft.rentResets, emptyRentReset()] })
                    }
                  >
                    +
                  </button>
                )}
              </label>
              <input
                id={fieldId("rentCommenceDate")}
                type="date"
                className="manage-input"
                disabled={readOnly}
                value={draft.rentCommenceDate}
                onChange={(e) => set("rentCommenceDate", e.target.value)}
              />
              {errors.rentCommenceDate && (
                <p className="tosp-field-error">{errors.rentCommenceDate}</p>
              )}
            </div>

            {numberField("rentPsfCommencement", "Rent PSF Commencement", "$")}
            {renewalSlot("tosp-col-3", "New Rent Commencement Date", (reset, index) => (
              <div
                key={reset.id}
                // The × is missing on an amendment's renewal, so its space is
                // held open — a stack where one date box runs 30px wider than
                // the one under it reads as a mistake.
                className={`tosp-renewal-entry${
                  !readOnly && reset.fromAmendment ? " tosp-renewal-locked" : ""
                }`}
              >
                <input
                  type="date"
                  aria-label={`New Rent Commencement Date ${index + 1}`}
                  className="manage-input tosp-renewal-input"
                  disabled={readOnly}
                  value={reset.rentCommenceDate}
                  onChange={(e) => setReset(reset.id, { rentCommenceDate: e.target.value })}
                  title={
                    reset.fromAmendment ? "Read from an amendment" : undefined
                  }
                />
                {/* No × on a renewal that came off an amendment. The figures
                    stay editable — a misread date has to be correctable — but
                    the row itself is a term of a signed document, and deleting
                    it would quietly drop a rent change the lease actually made.
                    Renewals added with the "+" beside Rent Commence Date are
                    the admin's own and keep their ×. */}
                {!readOnly && !reset.fromAmendment && (
                  <button
                    type="button"
                    className="tosp-inline-add tosp-inline-remove"
                    title={`Remove renewal ${index + 1}`}
                    onClick={() =>
                      onChange({
                        rentResets: draft.rentResets.filter((r) => r.id !== reset.id),
                      })
                    }
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* The commencement rate compounded by the annual increase, once per
              step-up month. Rent PSF Current is what's payable today; the two
              beside it are this calendar year's before and after figures. */}
          <div className="tosp-form-row-4">
            {renewalSlot("tosp-col-1", "New Rent PSF Commencement", (reset, index) => (
              <div key={reset.id} className="tosp-affix-wrap">
                <span className="tosp-affix tosp-affix-prefix">$</span>
                <input
                  className="manage-input tosp-has-prefix tosp-renewal-input"
                  autoComplete="off"
                  inputMode="decimal"
                  aria-label={`New Rent PSF ${index + 1}`}
                  disabled={readOnly}
                  value={reset.rentPsfCommencement}
                  onChange={(e) => setReset(reset.id, { rentPsfCommencement: e.target.value })}
                />
              </div>
            ))}
            {calcMoneyField(
              "Rent PSF Current",
              rentPsfToday(escalation),
              "The rate in force today — calculated",
              psfColumn(1),
              true,
            )}
            {calcMoneyField(
              "Rent PSF Pre-Step",
              rentPsfCurrent(escalation),
              "This calendar year's rate before its step-up — calculated",
              psfColumn(2),
            )}
            {calcMoneyField(
              "Rent PSF Post-Step",
              rentPsfStepUp(escalation),
              "This calendar year's rate from the step-up month onward — calculated",
              psfColumn(3),
            )}
          </div>

          {/* Both BMVA figures are read here and set elsewhere — the rate for
              the whole village under Configuration, the area on the unit. */}
          <div className="tosp-form-row-4">
            <div className="tosp-field">
              <span className="tosp-label">BMVA Rate PSF</span>
              <div
                className="tosp-calc-value"
                title="Set under Configuration → BMVA Fees, for every unit at once"
              >
                {unit ? formatCurrency(bmvaRate(configuration, unit)) : "—"}
              </div>
            </div>

            <div className="tosp-field">
              <span className="tosp-label">BMVA Occupancy SqFt</span>
              <div
                className="tosp-calc-value tosp-calc-value-live"
                title="Set on the unit — the area the basic fee is charged on"
              >
                {unit ? formatSqFt(unit.bmvaSqFt) : "—"}
              </div>
            </div>

            {/* BMVA is either the royalty on sales or a capped amount, never
                both. The royalty is BMVA's own rate and the same for everybody,
                so only the cap is entered here — naming one means this space
                pays that instead. */}
            <div className="tosp-field">
              <span className="tosp-label">BMVA Royalty</span>
              <div
                className="tosp-calc-value"
                title="Set under Configuration → BMVA Fees, for every tenant at once"
              >
                {bmvaCapUsed ? "— cap applies" : royalty === null ? "—" : `${royalty}%`}
              </div>
            </div>

            {/* ANNUAL, and said so: the budget spreads it over twelve, so a
                monthly figure typed here is out by a factor of twelve and
                nothing on the screen would show it. */}
            {numberField("bmvaCap", "BMVA Cap (annual)", "$")}
          </div>
        </div>
      )}
    </div>
  );
}
